import { createSlice } from '@reduxjs/toolkit'
import { Network } from 'common/api/v1/types'
import { logoutUser } from '../actions/userActions'
import { isOneOf } from '../actions'
import {
  clearNetwork,
  clearNetworks,
  createNetwork,
  getNetwork,
  getNetworks,
  removeNetwork,
  removeNetworks,
  updateNetwork,
  updateNetworks,
} from '../actions/networkActions'
import type { ApplicationException } from '../../api/ApplicationException'
import { createLoadingReducer } from './shared'

interface State {
  formErrors?: ApplicationException['errorInfo']['details']
  loading: boolean
  network?: Network
  networks: Array<Network>
  saving?: boolean
  total: number
}
export const initialStateNetworks: State = {
  networks: [],
  loading: false,
  total: 0,
}

const { isLoadingAction, loadingReducer } = createLoadingReducer<State>(removeNetwork, getNetworks)

const networksSlice = createSlice({
  name: 'networks',
  initialState: initialStateNetworks,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(getNetwork.fulfilled, (state, { payload: network }): State => ({ ...state, network }))
      .addCase(logoutUser.fulfilled, (): State => initialStateNetworks)
      .addCase(clearNetwork, (state): State => ({ ...state, network: undefined, formErrors: undefined }))
      .addCase(clearNetworks, (state): State => ({ ...state, networks: [] }))
      .addCase(
        getNetworks.fulfilled,
        (state, { payload: { items: networks, total } }): State => ({
          ...state,
          networks,
          total,
        }),
      )
      .addCase(
        removeNetworks.fulfilled,
        (state, { payload: ids }): State => ({
          ...state,
          networks: state.networks.filter((network) => !ids.includes(network.id)),
        }),
      )
      .addCase(
        updateNetworks.fulfilled,
        (state, { payload: updatedNetworks }): State => ({
          ...state,
          networks: state.networks.map((network) => {
            const updated = updatedNetworks.find((updated) => updated.id === network.id)
            return updated || network
          }),
        }),
      )
      .addMatcher(
        isOneOf([updateNetwork.pending, updateNetworks.pending, createNetwork.pending]),
        (state): State => ({ ...state, saving: true }),
      )
      .addMatcher(
        isOneOf([createNetwork.fulfilled, updateNetwork.fulfilled, updateNetworks.fulfilled]),
        (state): State => ({
          ...state,
          saving: undefined,
          formErrors: undefined,
        }),
      )
      .addMatcher(
        isOneOf([updateNetwork.rejected, updateNetworks.rejected, createNetwork.rejected]),
        (state, { payload }): State => ({
          ...state,
          saving: false,
          formErrors: payload?.details,
        }),
      )
      .addMatcher(isLoadingAction, loadingReducer)
  },
})

export default networksSlice.reducer
